import {
  ACTIVATION_COLORS,
  ACTIVATION_LABELS,
  ACTIVATION_ORDER,
  REASON_PRIORITY,
  ec2InstanceLabel,
  resolveSupportLabel,
} from '../classification';
import { clusterFilterKey, infrastructureBucket } from '../filterVolumes';
import { signalColorTokens } from '../sidebar/signalColors';
import type { CheckboxFilterOption } from '~/shared/components/filters/checkboxFilterOption';

import type { ExplorationVolume } from '../../types/exploration';

export type { CheckboxFilterOption } from '~/shared/components/filters/checkboxFilterOption';

export type SupportFilterGroupKey = 'supported' | 'unsupported';

export interface SupportFilterGroup {
  key: SupportFilterGroupKey;
  label: string;
  color: string;
  count: number;
  children?: CheckboxFilterOption[];
}

const SUPPORT_TOP_LEVEL_COLORS: Record<SupportFilterGroupKey, string> = {
  supported: signalColorTokens.statusStyles.supported.stroke,
  unsupported: '#DCDFE7',
};

const SUPPORT_TOP_LEVEL_EXCLUDED = new Set(['supported', 'unattached']);

function countBy<T>(items: T[], keyFn: (item: T) => string): Map<string, number> {
  const m = new Map<string, number>();
  items.forEach((item) => {
    const k = keyFn(item);
    m.set(k, (m.get(k) ?? 0) + 1);
  });
  return m;
}

export function buildActivationOptions(volumes: ExplorationVolume[]): CheckboxFilterOption[] {
  return ACTIVATION_ORDER.map((key) => ({
    key,
    label: ACTIVATION_LABELS[key],
    color: ACTIVATION_COLORS[key],
    count: volumes.filter((v) => v.activationStatus === key).length,
  })).filter((o) => o.count > 0);
}

function reasonLabel(key: string): string {
  return resolveSupportLabel(key);
}

function buildUnsupportedChildren(volumes: ExplorationVolume[]): CheckboxFilterOption[] {
  const counts = countBy(volumes, (v) => v.supportStatus);
  const presentKeys = [...counts.keys()].filter(
    (k) => !SUPPORT_TOP_LEVEL_EXCLUDED.has(k) && (counts.get(k) ?? 0) > 0
  );

  const orderedKeys = [
    ...REASON_PRIORITY.filter((k) => k !== 'unattached' && presentKeys.includes(k)),
    ...(presentKeys.includes('unknown') ? ['unknown'] : []),
    ...presentKeys
      .filter((k) => !REASON_PRIORITY.includes(k) && k !== 'unknown')
      .sort((a, b) => reasonLabel(a).localeCompare(reasonLabel(b))),
  ];

  return orderedKeys.map((key) => ({
    key,
    label: reasonLabel(key),
    count: counts.get(key) ?? 0,
  }));
}

export function buildSupportFilterGroups(volumes: ExplorationVolume[]): SupportFilterGroup[] {
  const groups: SupportFilterGroup[] = [];

  const supportedCount = volumes.filter((v) => v.supportStatus === 'supported').length;
  if (supportedCount > 0) {
    groups.push({
      key: 'supported',
      label: resolveSupportLabel('supported'),
      color: SUPPORT_TOP_LEVEL_COLORS.supported,
      count: supportedCount,
    });
  }

  const unsupportedChildren = buildUnsupportedChildren(volumes);
  if (unsupportedChildren.length > 0) {
    const unsupportedCount = unsupportedChildren.reduce((sum, child) => sum + child.count, 0);
    groups.push({
      key: 'unsupported',
      label: 'Unsupported',
      color: SUPPORT_TOP_LEVEL_COLORS.unsupported,
      count: unsupportedCount,
      children: unsupportedChildren,
    });
  }

  return groups;
}

/** Support groups filtered by query — reserved for future global Support search wiring. */
export function filterSupportGroupsByQuery(
  groups: SupportFilterGroup[],
  searchQuery: string
): SupportFilterGroup[] {
  const q = searchQuery.trim().toLowerCase();
  if (!q) return groups;

  return groups.flatMap((group) => {
    if (group.key === 'supported') {
      return group.label.toLowerCase().includes(q) ? [group] : [];
    }

    const children = group.children ?? [];
    const parentMatches = group.label.toLowerCase().includes(q);
    const matchingChildren = children.filter((child) => child.label.toLowerCase().includes(q));

    if (!parentMatches && matchingChildren.length === 0) return [];

    return [
      {
        ...group,
        children: parentMatches ? children : matchingChildren,
      },
    ];
  });
}

/** Whether any support group matches a query — reserved for future global Support search wiring. */
export function hasVisibleSupportFilterGroups(
  groups: SupportFilterGroup[],
  searchQuery: string
): boolean {
  return filterSupportGroupsByQuery(groups, searchQuery).length > 0;
}

export function toggleUnsupportedGroup(
  childKeys: string[],
  pendingSupportStatuses: string[]
): string[] {
  if (childKeys.length === 0) return pendingSupportStatuses;

  const someSelected = childKeys.some((key) => pendingSupportStatuses.includes(key));
  if (someSelected) {
    return pendingSupportStatuses.filter((status) => !childKeys.includes(status));
  }

  const toAdd = childKeys.filter((key) => !pendingSupportStatuses.includes(key));
  return [...pendingSupportStatuses, ...toAdd];
}

export function buildRegionOptions(volumes: ExplorationVolume[]): CheckboxFilterOption[] {
  const counts = countBy(volumes, (v) => v.region || '(unknown)');
  return [...counts.entries()]
    .map(([key, count]) => ({ key, label: key, count }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function buildNodeGroupOptions(volumes: ExplorationVolume[]): CheckboxFilterOption[] {
  const counts = countBy(volumes, (v) => v.nodeGroup || '(No Group)');
  return [...counts.entries()]
    .map(([key, count]) => ({ key, label: key, count }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function buildDiskTypeOptions(volumes: ExplorationVolume[]): CheckboxFilterOption[] {
  const counts = countBy(volumes, (v) => v.diskType || '(unknown)');
  return [...counts.entries()]
    .map(([key, count]) => ({ key, label: key, count }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function buildInstanceIdOptions(volumes: ExplorationVolume[]): CheckboxFilterOption[] {
  const counts = countBy(
    volumes.filter((v) => v.instanceId),
    (v) => v.instanceId as string
  );
  return [...counts.entries()]
    .map(([key, count]) => ({ key, label: key, count }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function buildClusterOptions(volumes: ExplorationVolume[]): CheckboxFilterOption[] {
  const counts = countBy(
    volumes.filter((v) => v.cluster || v.instanceId),
    clusterFilterKey
  );
  return [...counts.entries()]
    .map(([key, count]) => ({ key, label: key, count }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export const INFRASTRUCTURE_LABELS: Record<'cluster' | 'ec2' | 'unattached', string> = {
  cluster: 'K8s cluster',
  ec2: ec2InstanceLabel,
  unattached: 'Unattached',
};

export function buildInfrastructureOptions(volumes: ExplorationVolume[]): CheckboxFilterOption[] {
  const counts = countBy(volumes, infrastructureBucket);
  return (['cluster', 'ec2', 'unattached'] as const)
    .map((key) => ({ key, label: INFRASTRUCTURE_LABELS[key], count: counts.get(key) ?? 0 }))
    .filter((option) => option.count > 0);
}

export function buildVolumeIdOptions(volumes: ExplorationVolume[]): CheckboxFilterOption[] {
  const counts = countBy(volumes, (v) => v.ebsId || '(unknown)');
  return [...counts.entries()]
    .map(([key, count]) => ({ key, label: key, count }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export interface TagFilterOption extends CheckboxFilterOption {
  tagKey: string;
}

export function buildTagOptions(volumes: ExplorationVolume[]): TagFilterOption[] {
  const seen = new Map<string, TagFilterOption>();

  volumes.forEach((v) => {
    if (!v.tags) return;
    Object.entries(v.tags).forEach(([tagKey, val]) => {
      if (val == null || val === '') return;
      const id = `${tagKey}:${val}`;
      const existing = seen.get(id);
      if (existing) {
        existing.count += 1;
      } else {
        seen.set(id, {
          key: val,
          tagKey,
          label: `${tagKey}: ${val}`,
          count: 1,
        });
      }
    });
  });

  return [...seen.values()].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}
/**
 * Tag options keyed for the filter panel: `tagKey:value`, matching the id encoding that
 * `filterConverters` round-trips through `ExplorationFilter`. Previously this mapping was
 * inlined in VolumeTagsFilter and duplicated in its `buildVolumeTagFilterOptions` wrapper.
 */
export function buildTagFilterOptions(volumes: ExplorationVolume[]): CheckboxFilterOption[] {
  return buildTagOptions(volumes).map((option) => ({
    key: `${option.tagKey}:${option.key}`,
    label: option.label,
    count: option.count,
  }));
}

